import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";
import { t } from "ttag";

import type { CustomerReviewsBlock as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``CustomerReviewsBlock`` (defined in
 * ``server/cms/components/customer_reviews.py``). DOM mirrors the legacy
 * Django template ``cms/components/customer_reviews.html`` exactly:
 * optional average-stars row (which also flips on the
 * ``section-testimonials__inner--pdp`` modifier), headline, per-review
 * testimonials (text + author + per-review StarRating), and footer CTA.
 *
 * The legacy template applied ``{% round_to_half %}`` to ratings before
 * passing them to ``star-rating.html``; that step is now redundant because
 * ``StarRating``'s internal bucket thresholds
 * (``rating >= i + 0.75``, ``rating >= i + 0.25``) produce the same visual
 * output when passed the raw decimal value.
 *
 * The ``section-testimonials__body-testimonial-start-rating`` class name
 * carries a typo from the legacy template (``start`` instead of ``star``)
 * — preserved here because downstream CSS may key off it.
 *
 * The ``aria-label="reviews"`` attribute is hardcoded English in the
 * Django template; here it is wrapped in a ttag to make it translatable.
 *
 * The displayed average-stars number is the raw decimal value (e.g.
 * ``"4.5"``), not the rounded one — preserved from the legacy template.
 */
export const CustomerReviewsBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const reviews = v.reviews ?? [];
    const avgRating =
        v.average_stars !== null &&
        v.average_stars !== undefined &&
        v.average_stars !== ""
            ? Number(v.average_stars)
            : 0;
    // Mirror the legacy `{% if self.average_stars %}` guard: Django treats a
    // Decimal of 0/0.0 as falsy and omits the PDP modifier + average-rating
    // row. The serialized DecimalBlock is a string, so "0"/"0.0" are
    // non-empty and need an explicit numeric-zero check here.
    const hasAvgStars = avgRating !== 0;
    const reviewLinkUrl = v.review_link?.url ?? "";
    return (
        <div className="section-testimonials">
            <div
                className={`l-capped-width section-testimonials__inner${
                    hasAvgStars ? " section-testimonials__inner--pdp" : ""
                }`}
            >
                {hasAvgStars && (
                    <div className="section-testimonials__average-rating">
                        <StarRating
                            rating={avgRating}
                            className="section-testimonials__average-rating-stars"
                        />
                        <span className="section-testimonials__average-rating-number">
                            {v.average_stars}
                        </span>
                    </div>
                )}
                <div className="section-testimonials__head">
                    <h2>{v.headline ?? ""}</h2>
                </div>
                <div className="section-testimonials__body">
                    {reviews.map((review, i) => {
                        const reviewRating = review.stars
                            ? Number(review.stars)
                            : 0;
                        return (
                            <div
                                key={i}
                                className="section-testimonials__body-testimonial"
                                role="group"
                                aria-label={t`reviews`}
                            >
                                <p>{review.text}</p>
                                <span>{review.author}</span>
                                <StarRating
                                    rating={reviewRating}
                                    className="section-testimonials__body-testimonial-start-rating"
                                />
                            </div>
                        );
                    })}
                </div>
                <footer className="section-testimonials__foot">
                    <a
                        href={reviewLinkUrl}
                        className="button button-lochmara-pillows section-testimonials__footer-link"
                    >
                        {v.review_link_name ?? ""}
                    </a>
                </footer>
            </div>
        </div>
    );
};
